import { Program, ProgramCategory } from 'reducers/programs/types';
import { AvailableProgram } from 'reducers/availablePrograms/types';

export type CategorySection = {
  category: ProgramCategory;
  title: string;
  subtitle: string;
  owned: Program[];
  locked: AvailableProgram[];
};

const CATEGORY_META: Record<ProgramCategory, { title: string; subtitle: string }> = {
  [ProgramCategory.Foundation]: {
    title: 'Foundation',
    subtitle: 'Build Your Base of Strength, Mobility, and Body Control',
  },
  [ProgramCategory.Attributes]: {
    title: 'Attributes',
    subtitle: 'Build Range and Work Capacity Where You Need Them',
  },
  [ProgramCategory.Application]: {
    title: 'Application',
    subtitle: 'Develop Your Skill and Power for Dynamic Movement',
  },
  [ProgramCategory.MemberResources]: {
    title: 'Member Resources',
    subtitle: 'Articles, Coaching, and Community',
  },
  [ProgramCategory.Supplemental]: {
    title: 'Supplemental',
    subtitle: 'Address Specific Concerns and Goals with Targeted Training',
  },
  [ProgramCategory.Course]: {
    title: 'Courses',
    subtitle: 'Self-Paced Format Practical and Educational Content',
  },
  [ProgramCategory.Legacy]: {
    title: 'Legacy Programs',
    subtitle: 'Older programs and everything else',
  },
};

const CATEGORY_ORDER: ProgramCategory[] = [
  ProgramCategory.Foundation,
  ProgramCategory.Attributes,
  ProgramCategory.Application,
  ProgramCategory.MemberResources,
  ProgramCategory.Supplemental,
  ProgramCategory.Course,
  ProgramCategory.Legacy,
];

const resolveCategory = (category: ProgramCategory | null | undefined): ProgramCategory =>
  category && CATEGORY_ORDER.includes(category) ? category : ProgramCategory.Legacy;

const groupProgramsByCategory = (
  owned: Program[] | null | undefined,
  locked: AvailableProgram[] | null | undefined = [],
): CategorySection[] => {
  const ownedBuckets: Record<ProgramCategory, Program[]> = {
    [ProgramCategory.Foundation]: [],
    [ProgramCategory.Attributes]: [],
    [ProgramCategory.Application]: [],
    [ProgramCategory.MemberResources]: [],
    [ProgramCategory.Supplemental]: [],
    [ProgramCategory.Course]: [],
    [ProgramCategory.Legacy]: [],
  };

  const lockedBuckets: Record<ProgramCategory, AvailableProgram[]> = {
    [ProgramCategory.Foundation]: [],
    [ProgramCategory.Attributes]: [],
    [ProgramCategory.Application]: [],
    [ProgramCategory.MemberResources]: [],
    [ProgramCategory.Supplemental]: [],
    [ProgramCategory.Course]: [],
    [ProgramCategory.Legacy]: [],
  };

  owned?.forEach((program) => {
    ownedBuckets[resolveCategory(program.category)].push(program);
  });

  locked?.forEach((program) => {
    lockedBuckets[resolveCategory(program.category)].push(program);
  });

  return CATEGORY_ORDER.filter(
    (category) => ownedBuckets[category].length > 0 || lockedBuckets[category].length > 0,
  ).map((category) => ({
    category,
    title: CATEGORY_META[category].title,
    subtitle: CATEGORY_META[category].subtitle,
    owned: ownedBuckets[category],
    locked: lockedBuckets[category],
  }));
};

export default groupProgramsByCategory;
